import { useEffect } from "react";
import { findBrandByDomain } from "../../config/brands.ts";
import { installDevFetchInterceptor } from "./auth-interceptor.ts";
import {
  syncHeadMetadata,
  syncHeaderElements,
  syncAuthStatusIndicator,
  syncTagServerDisplay,
  renderBrandFooter,
  applyBrandThemeColors,
  syncSidebarCollapse,
} from "./dom-sync.ts";

/**
 * Sincronizador de marca en cliente para el modo white-label
 * Zudoku prerenderiza con la marca primaria para evitar desajustes de
 * hidratación; tras hidratar, este componente ajusta el DOM visible
 * (título, favicon, logo, enlaces y tema) según el hostname real
 */
export function HostnameBrandSync() {
  useEffect(() => {
    // El interceptor toca window.fetch: solo existe en cliente, se instala aquí
    installDevFetchInterceptor();
    const applyBrand = () => {
      document.documentElement.classList.remove("dark");
      document.documentElement.setAttribute("data-theme", "light");

      const brand = findBrandByDomain(window.location.hostname);
      if (!brand) return;

      // Propaga el servidor API de la marca al store de servidor de Zudoku
      if (brand.api?.serverUrl) {
        try {
          const currentStore = localStorage.getItem("zudoku-selected-server");
          const parsed = currentStore ? JSON.parse(currentStore) : {};
          if (parsed?.state?.selectedServer !== brand.api.serverUrl) {
            localStorage.setItem(
              "zudoku-selected-server",
              JSON.stringify({
                state: { selectedServer: brand.api.serverUrl },
                version: 0,
              }),
            );
          }
        } catch {
          //ignored
        }
      }

      syncHeadMetadata(brand);
      syncHeaderElements(brand);
      renderBrandFooter(brand);
      applyBrandThemeColors(brand, document.documentElement.classList.contains("dark"));
      syncSidebarCollapse();
    };

    applyBrand();

    const hydrationTimers = [100, 300, 500, 1200].map((delay) =>
      window.setTimeout(() => {
        applyBrand();
        syncTagServerDisplay();
      }, delay),
    );

    let syncQueued = false;
    const domObserver = new MutationObserver(() => {
      if (syncQueued) return;
      syncQueued = true;
      queueMicrotask(() => {
        syncQueued = false;
        syncTagServerDisplay();
        syncAuthStatusIndicator();
      });
    });
    domObserver.observe(document.body, { childList: true, subtree: true });

    const onAuthChanged = () => {
      syncAuthStatusIndicator();
    };
    window.addEventListener("api_auth_changed", onAuthChanged);
    window.addEventListener("storage", onAuthChanged);

    const observer = new MutationObserver(() => {
      const brand = findBrandByDomain(window.location.hostname);
      if (brand) {
        applyBrandThemeColors(brand, document.documentElement.classList.contains("dark"));
      }
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

    return () => {
      hydrationTimers.forEach((timer) => window.clearTimeout(timer));
      domObserver.disconnect();
      window.removeEventListener("api_auth_changed", onAuthChanged);
      window.removeEventListener("storage", onAuthChanged);
      observer.disconnect();
    };
  }, []);

  return null;
}